import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  Avatar,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Chip,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { Product, Variant } from "../types";
import { formatAttributes as fmtAttrs, safeParseAttributes } from "../utils/variantAttributes";


interface QuickAddListProps {
  quickAddProducts: Product[];
  onRemoveFromQuickAdd: (productId: number) => void;
  onAddToOrder: (variant: Variant) => void;
}

const QuickAddList: React.FC<QuickAddListProps> = ({
  quickAddProducts = [],
  onRemoveFromQuickAdd,
  onAddToOrder,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const theme = useTheme();

  const groupedProducts = quickAddProducts.reduce((acc, product) => {
    const category = product.category_name?.trim() || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDialogOpen(true);
    setSearchQuery(""); // Reset search when opening new dialog
  };

  const handleVariantSelect = (variant: Variant) => {
    onAddToOrder(variant);
    setIsDialogOpen(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedProduct(null);
  };

  const formatAttributes = (variant: Variant) =>
    fmtAttrs(variant.attributes, { sep: " / ", empty: "" });


  const filteredVariants = useMemo(() => {
    if (!selectedProduct) return [];
    return selectedProduct.variants
      ?.filter(variant => {
        const searchLower = searchQuery.toLowerCase();
        const attrsText = fmtAttrs(variant.attributes, { sep: " / ", empty: "" }).toLowerCase();

        return (
          (variant.sku || "").toLowerCase().includes(searchLower) ||
          attrsText.includes(searchLower)
        );

      })
      ?.sort((a, b) => {
        const aText = formatAttributes(a) || a.sku;
        const bText = formatAttributes(b) || b.sku;
        return aText.localeCompare(bText);
      }) || [];
  }, [selectedProduct, searchQuery]);

  const attributeNames = useMemo(() => {
    if (!selectedProduct) return [];
    const names = new Set<string>();
    selectedProduct.variants?.forEach(variant => {
      variant.attributes?.forEach(attr => names.add(attr.name));
    });
    return Array.from(names);
  }, [selectedProduct]);

  return (
    <Box sx={{
      maxWidth: 800,
      margin: '0 auto',
      p: 2,
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[1]
    }}>
      <Typography
        variant="h6"
        sx={{
          mb: 2,
          fontWeight: 700,
          color: 'text.primary',
          letterSpacing: 0.5
        }}
      >
        Quick Add
      </Typography>

      {Object.entries(groupedProducts).map(([category, products]) => (
        <Accordion
          key={category}
          sx={{
            mb: 1,
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
            '&:before': { display: 'none' },
            borderRadius: '12px!important',
            overflow: 'hidden',
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            backdropFilter: 'blur(10px)',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
            sx={{
              minHeight: '56px!important',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05)
              },
            }}
          >
            <Typography variant="subtitle1" sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}>
              {category}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1, pb: 2, px: 1 }}>
            <Grid container spacing={1.5}>
              {products.map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.product_id}>
                  <Card
                    onClick={() => handleProductClick(product)}
                    sx={{
                      cursor: "pointer",
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: theme.shadows[4],
                        borderColor: theme.palette.primary.main,
                        '& .delete-btn': { opacity: 1 }
                      },
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="140"
                      image={product.image_url || "/placeholder-image.png"}
                      alt={product.name}
                      sx={{
                        objectFit: 'cover',
                        backgroundColor: theme.palette.grey[100]
                      }}
                    />
                    <Box sx={{
                      p: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: 'center',
                      backgroundColor: theme.palette.background.paper
                    }}>
                      <Typography variant="body2" noWrap sx={{
                        fontWeight: 500,
                        color: 'text.primary'
                      }}>
                        {product.name}
                      </Typography>
                      <IconButton
                        size="small"
                        className="delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveFromQuickAdd(product.product_id);
                        }}
                        sx={{
                          opacity: 0,
                          transition: 'opacity 0.2s',
                          color: theme.palette.error.main,
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1)
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      {selectedProduct && (
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: '16px',
              backgroundImage: 'none',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`
            }
          }}
        >
          <DialogTitle sx={{
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
                {selectedProduct.name}
              </Typography>
              {attributeNames.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {attributeNames.map(name => (
                    <Chip
                      key={name}
                      label={name}
                      size="small"
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        fontWeight: 500
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <TextField
              fullWidth
              placeholder="Search variants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                sx: {
                  borderRadius: 4,
                  backgroundColor: theme.palette.background.default,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                  }
                }
              }}
              sx={{ mb: 2 }}
            />

            {filteredVariants.length > 0 ? (
              <List disablePadding>
                {filteredVariants.map((variant) => (
                  <ListItem
                    key={variant.id}
                    onClick={() => handleVariantSelect(variant)}
                    sx={{
                      cursor: "pointer",
                      borderRadius: '12px',
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                        borderLeft: `3px solid ${theme.palette.primary.main}`
                      },
                      '& + &': { mt: 0.5 },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Avatar
                      src={variant.image_url || selectedProduct.image_url || ""}
                      alt={selectedProduct.name}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: theme.palette.grey[100]
                      }}
                    />
                    <Box sx={{ ml: 1.5, flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {formatAttributes(variant) || variant.sku}
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            color: 'primary.main',
                            fontWeight: 700
                          }}
                        >
                          ${variant.price?.toFixed(2) || 'N/A'}
                        </Typography>
                      </Typography>
                      {/* <Typography variant="body2" sx={{ 
                        color: 'text.secondary', 
                        fontSize: '0.75rem',
                        mt: 0.5
                      }}>
                        SKU: {variant.sku || "No SKU"}
                      </Typography> */}
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{
                py: 4,
                textAlign: 'center',
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                borderRadius: 2
              }}>
                <Typography variant="body1" color="text.secondary">
                  No matching variants found
                </Typography>
              </Box>
            )}
            <Box sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  px: 3,
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default QuickAddList;